/**
 * Semantic design tokens for TaskQ.
 * These tokens provide semantic color mappings that work with both light and dark themes.
 * Use with MUI's `sx` prop: sx={{ bgcolor: tokens.status.todo.bg }}
 *
 * For theme-aware colors, use CSS variables: 'var(--mui-palette-primary-main)'
 * For static semantic colors (status chips, priority icons), use these tokens directly.
 */

// Task status colors — used in board columns, chips, selects
export const statusColors = {
  todo: {
    bg: 'var(--taskq-status-todo-bg, #f0f0f3)',
    color: 'var(--taskq-status-todo-color, #80838d)',
    label: 'To Do',
  },
  in_progress: {
    bg: 'var(--taskq-status-inprogress-bg, #e0edff)',
    color: 'var(--taskq-status-inprogress-color, #0d74ce)',
    label: 'In Progress',
  },
  in_review: {
    bg: 'var(--taskq-status-inreview-bg, #fff3d0)',
    color: 'var(--taskq-status-inreview-color, #ab6400)',
    label: 'In Review',
  },
  done: {
    bg: 'var(--taskq-status-done-bg, #ddf3e4)',
    color: 'var(--taskq-status-done-color, #218358)',
    label: 'Done',
  },
};

// Labor/work item status colors
export const laborStatusColors = {
  hold: {
    bg: 'var(--taskq-labor-hold-bg, #f0f0f3)',
    color: 'var(--taskq-labor-hold-color, #80838d)',
    label: 'Hold',
  },
  pending: {
    bg: 'var(--taskq-labor-pending-bg, #fff3d0)',
    color: 'var(--taskq-labor-pending-color, #ab6400)',
    label: 'Pending',
  },
  in_progress: {
    bg: 'var(--taskq-labor-inprogress-bg, #e0edff)',
    color: 'var(--taskq-labor-inprogress-color, #0d74ce)',
    label: 'In Progress',
  },
  complete: {
    bg: 'var(--taskq-labor-complete-bg, #ddf3e4)',
    color: 'var(--taskq-labor-complete-color, #218358)',
    label: 'Complete',
  },
};

// Priority icon colors (Jira-style arrows)
export const priorityColors = {
  low: '#2d8738',
  medium: '#e97f33',
  high: '#e5493a',
  critical: '#ce350f',
};

// Task type icon colors
export const typeColors = {
  bug: '#e5484d',
  story: '#30a46c',
  task: '#0090ff',
};

// User role colors
export const roleColors = {
  admin: { bg: '#f3e8ff', color: '#6e56cf' },
  manager: { bg: '#e0edff', color: '#3e63dd' },
  member: { bg: '#f0f0f3', color: '#60646c' },
};

// Surfaces — use these for custom surface layers beyond MUI's background.default/paper
export const surfaces = {
  // Elevated surface (kanban columns, table headers, sidebar sections)
  elevated: 'var(--taskq-surface-elevated, #f0f0f3)',
  // Overlay surface (hover states, selected rows)
  overlay: 'var(--taskq-surface-overlay, #e8e8ec)',
  // Subtle surface (alternating rows, code blocks)
  subtle: 'var(--taskq-surface-subtle, #f9f9fb)',
};

// Avatar colors — consistent palette for user avatars
export const avatarColors = [
  '#6e56cf', // violet
  '#3e63dd', // indigo
  '#0090ff', // blue
  '#30a46c', // green
  '#e5484d', // red
  '#e97f33', // orange
  '#ab6400', // amber
  '#0d74ce', // cyan
];

/**
 * Get a consistent avatar color based on a string (userId, name, etc.)
 */
export const getAvatarColor = (str) => {
  if (!str) return avatarColors[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};
